import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { BUFFER_TIMING, FINAL_BUFFER_TIMING } from '../../../../utils/URL';
import { showNotification } from '../../../action/NotificationAction';
import store from '../../../../sessionStorage';
import { timerLimitMessage } from '../../../../utils/URL';
import {
    BroadCastSession,
    ClassroomSession,
    ConferenceCall,
} from '../../../../utils/common/NamingTerms';

const SHOW_SESSION_END_NOTIFICATION = false;

/**
 * @package VirtuOwl
 * @type {React.ComponentClass}
 */
class ActiveVisitTimerOption extends Component {
    static propTypes = {
        showEncounter: PropTypes.func,
        showNotification: PropTypes.func,
        videoCallDetails: PropTypes.any,
    };
    constructor(props) {
        super(props);
        this.state = {
            isStarted: false,
            elapsedSeconds: this.getDurationSeconds(props),
            timerSessionKey: this.getTimerSessionKey(props),
        };
    }

    // shouldComponentUpdate
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.videoCallDetails !== nextProps.videoCallDetails ||
            this.state.elapsedSeconds !== nextState.elapsedSeconds ||
            this.state.timerSessionKey !== nextState.timerSessionKey
        );
    }

    componentDidMount() {
        this.startElapsedTimer();
        this.startSessionLimitTimer();
    }

    componentDidUpdate(prevProps) {
        const timerSessionKey = this.getTimerSessionKey(this.props);
        const prevTimerSessionKey = this.getTimerSessionKey(prevProps);
        const prevDurationSeconds = this.getDurationSeconds(prevProps);
        const durationSeconds = this.getDurationSeconds(this.props);

        if (prevDurationSeconds > 0 && durationSeconds <= 0) {
            this.handleSessionLimitReached();
        }

        if (timerSessionKey !== prevTimerSessionKey) {
            this.sessionLimitHandled = false;
            this.startSessionLimitTimer();
            this.setState({ elapsedSeconds: durationSeconds, timerSessionKey });
            return;
        }

        if (durationSeconds > 0 && durationSeconds !== prevDurationSeconds) {
            this.sessionLimitHandled = false;
            this.startSessionLimitTimer();
        }

        if (durationSeconds > this.state.elapsedSeconds) {
            this.setState({ elapsedSeconds: durationSeconds });
        }
    }

    componentWillUnmount() {
        clearInterval(this.elapsedTimer);
        clearTimeout(this.sessionLimitTimer);
        clearInterval(this.bufferTimer);
        clearInterval(this.finalTimer);
    }

    getDurationSeconds = (props = this.props) => {
        const durationLeft =
            props.videoCallDetails?.activeSession?.durationLeft ??
            props.videoCallDetails?.durationLeft ??
            0;
        const durationSeconds = Number(durationLeft);

        return Number.isFinite(durationSeconds) && durationSeconds > 0
            ? Math.floor(durationSeconds)
            : 0;
    };

    getTimerSessionKey = (props = this.props) => {
        const activeSession = props.videoCallDetails?.activeSession || {};

        return (
            activeSession.opentokSession ||
            props.videoCallDetails?.opentokSession ||
            activeSession.sessionId ||
            props.videoCallDetails?.sessionId ||
            ''
        );
    };

    startElapsedTimer = () => {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = setInterval(() => {
            this.setState((prevState) => ({ elapsedSeconds: prevState.elapsedSeconds + 1 }));
        }, 1000);
    };

    startSessionLimitTimer = () => {
        clearTimeout(this.sessionLimitTimer);
        const durationSeconds = this.getDurationSeconds();

        if (durationSeconds > 0) {
            this.sessionLimitTimer = setTimeout(() => {
                this.handleSessionLimitReached();
            }, durationSeconds * 1000);
        }
    };

    handleSessionLimitReached = () => {
        if (this.sessionLimitHandled) {
            return;
        }

        this.sessionLimitHandled = true;
        if (SHOW_SESSION_END_NOTIFICATION) {
            this.props.showNotification(timerLimitMessage, 'error');
        }
        this.handleBufferTiming();
    };

    formatElapsedTime = (totalSeconds) => {
        const normalizedSeconds = Math.max(0, Number(totalSeconds) || 0);
        const hours = Math.floor(normalizedSeconds / 3600);
        const minutes = Math.floor((normalizedSeconds % 3600) / 60);
        const seconds = normalizedSeconds % 60;
        const pad = (value) => (value > 9 ? value : '0' + value);

        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    };

    // handleBufferFinalCompletion
    handleBufferFinalCompletion = () => {
        this.finalTimer = setInterval(() => {
            this.handleBufferTiming();
        }, 30000);
    };

    // handleBufferTiming
    handleBufferTiming = () => {
        const { studentDetails } = this.props;

        const currentTime = moment(Date.now()).format('YYYY-MM-DD HH:mm:ss');
        const fiveMinuteBufferTime = moment(
            studentDetails?.isOnDemand ||
                studentDetails?.sessionType === ClassroomSession ||
                studentDetails?.sessionType === BroadCastSession ||
                studentDetails?.sessionType === ConferenceCall
                ? studentDetails?.onDemand && studentDetails?.onDemand.endTime
                : studentDetails?.appointment && studentDetails?.appointment.endTime
        )
            .add(BUFFER_TIMING, 'minutes')
            .format('YYYY-MM-DD HH:mm:ss');
        const FinalBufferTime = moment(
            studentDetails?.isOnDemand ||
                studentDetails?.sessionType === ClassroomSession ||
                studentDetails?.sessionType === BroadCastSession ||
                studentDetails?.sessionType === ConferenceCall
                ? studentDetails?.onDemand && studentDetails?.onDemand.endTime
                : studentDetails?.appointment && studentDetails?.appointment.endTime
        )
            .add(FINAL_BUFFER_TIMING, 'minutes')
            .format('YYYY-MM-DD HH:mm:ss');
        if (FinalBufferTime < currentTime) {
            clearInterval(this.finalTimer);
            this.props.openVideoCallFinalBufferAlert();
        } else {
            if (fiveMinuteBufferTime > currentTime) {
                this.bufferTimer = setInterval(() => {
                    this.handleBufferFirstCompletion(fiveMinuteBufferTime);
                }, 30000);
            } else {
                if (FinalBufferTime > currentTime) {
                    this.handleBufferFinalCompletion();
                }
            }
        }
    };

    // handleBufferFirstCompletion
    handleBufferFirstCompletion = (fiveMinuteBufferTime) => {
        const currentTime = moment(Date.now()).format('YYYY-MM-DD HH:mm:ss');
        if (fiveMinuteBufferTime < currentTime) {
            clearInterval(this.bufferTimer);
            this.props.openVideoCallBufferAlert();
            this.handleBufferTiming();
        }
    };

    render() {
        const { videoCallDetails } = this.props;
        return (
            <>
                {store.get('VISIT_TYPE') !== 'class_room' &&
                store.get('VISIT_TYPE') !== 'broadcast' ? (
                    <ul
                        style={{
                            float: 'left',
                            backgroundColor: '#FFFFFF1A',
                            color: 'white',
                        }}
                        className="fileAdding-disabled p-2 rounded-full"
                    >
                        <li>{this.formatElapsedTime(this.state.elapsedSeconds)}</li>
                    </ul>
                ) : (
                    <ul
                        style={{
                            float: 'left',
                            backgroundColor: '#FFFFFF1A',
                            color: 'white',
                        }}
                        className="fileAdding-disabled p-2 rounded-full"
                    >
                        {videoCallDetails?.activeSession && (
                            <li>{this.formatElapsedTime(this.state.elapsedSeconds)}</li>
                        )}
                    </ul>
                )}
            </>
        );
    }
}

const mapStatetoProps = (state) => {
    return {};
};

const mapDispatchtoProps = (dispatch) => {
    return {
        showNotification: (message, type) => {
            dispatch(showNotification(message, type));
        },
    };
};

export default connect(mapStatetoProps, mapDispatchtoProps)(ActiveVisitTimerOption);
